/**
 * 首页「行动层」三张卡（2026-10-09）—— 生产漏斗 / 待处理 / 准备度。
 *
 * ## 为什么要专门一层「行动卡」
 *
 * 改版前这一层是**环形占比 + 横向排行**：两张都是「看板型」——
 * 它们描述现状，但不指向动作。用户看完知道「产出中 8 个」「天地崩 1098 镜」，
 * 然后呢？没有任何一格能回答「我现在该去做什么」。
 *
 * 这三张卡各自绑定一个明确动作：
 *   · 生产漏斗 → 「卡在哪一级」（补数据还是补算力）
 *   · 待处理   → 「先去修哪个项目」（带跳转）
 *   · 准备度   → 「前置资产能不能开工」
 *
 * ## 🔴 与「运行状态」卡的分工（最容易混淆的一对）
 *
 *   运行状态卡回答「**服务**活着吗」（门户进程 / 看门狗 / ComfyUI / 作业队列）；
 *   这三张卡回答「**活干得出来吗**」（镜头失败 380、配音就绪 1/2317）。
 * 两者正交：服务全绿而产线大面积失败是常态。只有前者时，这种状态会被显示成一片正常。
 *
 * ## 🔴 每个数字都必须能追到一条 SQL
 *
 * 三张卡的数据全部来自 `lib/home/queries.ts` 里被 `project_id` 约束的相关子查询
 * （见该文件顶部第一纪律）。这里**不允许**再算任何派生指标 ——
 * 视图层一算，口径就出现了第二个事实源。
 */

"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import { F } from "@/components/studio/ui";
import type { HomeFunnel, HomePending, HomeReadiness } from "@/lib/home/types";

import { toneColor } from "./charts";
import { EmptyIcon, HomeEmpty } from "./home-primitives";

/**
 * 百分比。
 *
 * 🔴 两种「看起来是 0」必须区分开：
 *   · 分母为 0 → `—`（无可算）；
 *   · 分子为 0 → `0%`（真的一件都没做）；
 *   · 分子非 0 但不足 1% → `<1%`。配音实测 1/2317 = 0.04%，四舍五入成 `0%`
 *     与「一条都没做」在界面上完全同形，而这恰好是最需要被看见的一条链路。
 */
function pct(done: number, total: number): string {
  if (total <= 0) return "—";
  if (done <= 0) return "0%";
  const v = (done / total) * 100;
  if (v < 1) return "<1%";
  return `${Math.round(v)}%`;
}

// ── 生产漏斗 ────────────────────────────────────────────────────

export function FunnelCard({
  funnel,
  labelOf,
  emptyText,
}: {
  funnel: HomeFunnel;
  /** 数据层键（带 `home.` 前缀）→ 文案。必须由调用方用**根翻译器**传入 */
  labelOf: (key: string) => string;
  emptyText: string;
}) {
  const t = useTranslations("home");
  const stages = funnel.stages;
  if (stages.length === 0) {
    return <HomeEmpty icon={<EmptyIcon />} title={emptyText} compact />;
  }

  // 第一级 = 100% 基准。用「占首个阶段的比例」而不是「占最大值」：
  // 漏斗的意义就是看逐级收窄，基准换了图形就不再是漏斗。
  const base = Math.max(1, stages[0]?.value ?? 1);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5">
      <div className="flex flex-1 flex-col">
        {stages.map((s) => (
          <div
            key={s.id}
            // 🔴 行高固定 + 列宽固定：漏斗是**逐行对齐**的图，任何一行的
            //    内容多寡都不许改变列的位置。首版让「值 / 徽标 / 转化率」三列
            //    按内容自适应，于是有徽标的行把转化率挤右、没徽标的行留 38px 空槽 ——
            //    五行的百分比读不成一列，漏斗的「逐级收窄」就看不出来了。
            // 🔴 flex-1 + min-h-6：纵向的多余高度由**行高均分**吃掉（行距变宽），
            //    而不是在整组上下留白 —— 后者会让首行与邻卡首行错位。
            className="group flex min-h-6 min-w-0 flex-1 items-center gap-1.5"
            data-funnel-stage={s.id}
            title={s.gap > 0 ? t("funnel.gapTip", { count: s.gap }) : undefined}
          >
            <span className={`${F.deco} w-[62px] shrink-0 truncate`}>{labelOf(s.labelKey)}</span>

            {/* 条形：轨道常驻（否则短条看起来像漂浮的色块），填充按基准比例 */}
            <span className="relative h-2.5 min-w-0 flex-1 overflow-hidden rounded-[3px] bg-[var(--surface)]">
              <span
                className="absolute inset-y-0 left-0 rounded-[3px] transition-[width]"
                style={{
                  width: `${Math.max(1.5, (s.value / base) * 100)}%`,
                  background: toneColor(s.tone),
                  opacity: 0.85,
                }}
              />
            </span>

            <span className="w-[46px] shrink-0 text-right text-xs font-medium tabular-nums text-[var(--text-primary)]">
              {s.value.toLocaleString()}
            </span>

            {/* 失败槽**恒占位**（44px）：有徽标时是红字，没有时留空 ——
                这样「失败」与「转化率」各自成一列，五行竖直对齐。
                blocked 为 null = 这一级没有失败信号源，**不给徽标也留同宽空槽**，
                绝不用 0 冒充「没有阻塞」（那是把「不知道」写成「一切正常」）。 */}
            <span className="flex w-[74px] shrink-0 justify-end">
              {s.blocked !== null && s.blocked > 0 ? (
                <Link
                  href={s.blockedHref ?? "/pipeline"}
                  className="rounded bg-[var(--destructive)]/10 px-1 text-[10px] leading-4 tabular-nums text-[var(--destructive)] transition-colors hover:bg-[var(--destructive)]/20"
                  title={t("funnel.blockedTip", { count: s.blocked })}
                >
                  {t("funnel.blocked", { count: s.blocked })}
                </Link>
              ) : null}
            </span>

            <span className={`${F.deco} w-[34px] shrink-0 text-right tabular-nums`}>
              {s.rate === null ? "" : pct(s.value, Math.round(s.value / s.rate))}
            </span>
          </div>
        ))}
      </div>

      {/* 项目阶段占比：原独立环图并入此处。14 个项目的环图信息密度太低，
          一行图例（点 + 名 + 数）在同样一眼里给完了同样的信息，还省下一整格。 */}
      {funnel.stageShare.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-[var(--border-subtle)] pt-1.5">
          <span className={`${F.deco} shrink-0`}>{t("funnel.stageShare")}</span>
          {funnel.stageShare.map((s) => (
            <span key={s.id} className="flex items-center gap-1" data-stage-slice={s.id}>
              <span
                aria-hidden
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: toneColor(s.tone) }}
              />
              <span className={F.deco}>{labelOf(s.labelKey)}</span>
              <span className="text-[10px] leading-none tabular-nums text-[var(--text-secondary)]">
                {s.value}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── 待处理 ──────────────────────────────────────────────────────

export function PendingCard({
  pending,
  labelOf,
  emptyText,
  allClearText,
}: {
  pending: HomePending;
  labelOf: (key: string) => string;
  emptyText: string;
  allClearText: string;
}) {
  const t = useTranslations("home");
  const locale = useLocale();
  const { buckets, topProjects } = pending;

  // 🔴 全绿态也要渲染：`{buckets.length && ...}` 会让整块消失、页面出现死区，
  // 用户读作「页面坏了」而不是「今天没有待处理」。
  if (buckets.length === 0 && topProjects.length === 0) {
    return (
      <HomeEmpty
        icon={<CheckCircle2 className="h-5 w-5 text-[var(--success)]" />}
        title={allClearText}
        compact
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* 分类计数 + 「去处理」。放在最上面：先给「有多少」，再给「在哪个项目」。
          行高与列宽固定 —— 四行的「去处理」必须竖直对齐成一列，
          否则每行的链接位置都不同，扫视时找不到落点。 */}
      <div className="flex flex-col">
        {buckets.map((b) => (
          <div
            key={b.id}
            className="flex h-6 min-w-0 items-center gap-1.5"
            data-pending-bucket={b.id}
          >
            <span
              aria-hidden
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ background: toneColor(b.tone) }}
            />
            <span className={`${F.deco} min-w-0 flex-1 truncate`}>{labelOf(b.labelKey)}</span>
            <span className="w-[42px] shrink-0 text-right text-sm font-semibold tabular-nums text-[var(--destructive)]">
              {b.value.toLocaleString()}
            </span>
            <Link
              href={b.href}
              className="flex w-[52px] shrink-0 items-center justify-end gap-0.5 rounded px-1 text-[10px] text-[var(--primary)] transition-colors hover:bg-[var(--primary)]/10"
            >
              {t("pending.go")}
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          </div>
        ))}
        {buckets.length === 0 ? <p className={`${F.deco}`}>{allClearText}</p> : null}
      </div>

      {topProjects.length > 0 ? (
        <div className="flex flex-col gap-0.5 border-t border-[var(--border-subtle)] pt-1.5">
          <p className={`${F.deco}`}>{t("pending.byProject")}</p>
          {topProjects.map((p) => (
            <Link
              key={p.id}
              // 「{locale}」占位按当前语言落地 → 项目分级管理页（2026-10-09 用户拍板）
              href={p.href.replace("{locale}", locale)}
              data-pending-project={p.id}
              className="group flex h-6 min-w-0 items-center gap-2 rounded px-1 transition-colors hover:bg-[var(--surface)]"
            >
              <span className="min-w-0 flex-1 truncate text-xs text-[var(--text-primary)]">
                {p.label}
              </span>
              {/* 明细用 i18n 句子拼，不在数据层拼字符串（见 PendingProject.detail 说明）。
                  🔴 定宽 + 右对齐：四条明细的「镜头 N · 场景 N」字数不同（是 0 还是 126），
                  不定宽就会把右边的合计推来推去，四个总数读不成一列。 */}
              <span className={`${F.deco} w-[104px] shrink-0 truncate text-right tabular-nums`}>
                {t("pending.shots", { count: p.detail.shots })} · {t("pending.scenes", { count: p.detail.scenes })}
              </span>
              <span className="w-[34px] shrink-0 text-right text-sm font-semibold tabular-nums text-[var(--text-secondary)] group-hover:text-[var(--primary)]">
                {p.value}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className={`${F.deco}`}>{emptyText}</p>
      )}
    </div>
  );
}

// ── 准备度 ──────────────────────────────────────────────────────

export function ReadinessCard({
  readiness,
  labelOf,
  emptyText,
}: {
  readiness: HomeReadiness;
  labelOf: (key: string) => string;
  emptyText: string;
}) {
  const items = readiness.items;
  if (items.length === 0) {
    return <HomeEmpty icon={<EmptyIcon />} title={emptyText} compact />;
  }

  return (
    // 🔴 纵向对齐（2026-10-09）：本卡只有 4 行，明细行的高度却由 agent 卡
    //    （min-h-13rem）决定 —— 顶对齐之后多余高度会全部沉到卡片底部，
    //    表现为「四条进度条挤在上半张卡、下半张白」。所以让 4 项**均分**卡片高度
    //    （每项 flex-1 + 组内居中），条与条之间的行距随卡片长高，
    //    与漏斗行的处理同构：留白去该去的地方，而不是堆在底部。
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2.5">
      {items.map((it) => {
        const ratio = it.total > 0 ? it.done / it.total : 0;
        const row = (
          <div className="flex h-full min-w-0 flex-col justify-center">
            <div className="flex min-w-0 items-baseline gap-2">
              <span className={`${F.deco} min-w-0 flex-1 truncate`}>{labelOf(it.labelKey)}</span>
              {/* 分子/分母定宽右对齐：四条的分母从 127 到 2317，不定宽则数字列参差 */}
              <span className="w-[74px] shrink-0 text-right text-xs tabular-nums text-[var(--text-secondary)]">
                {it.done.toLocaleString()}
                <span className="text-[var(--text-muted)]">/{it.total.toLocaleString()}</span>
              </span>
              <span className="w-[34px] shrink-0 text-right text-xs font-medium tabular-nums text-[var(--text-primary)]">
                {pct(it.done, it.total)}
              </span>
            </div>
            {/* 进度条：轨道 + 填充。填充色统一 primary —— 这里表达「完成度」，
                不是「类别」，给每条不同颜色会让人以为色差有含义。 */}
            <span className="relative mt-1 block h-1.5 overflow-hidden rounded-full bg-[var(--surface)]">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-[var(--primary)]"
                style={{ width: `${Math.max(1, ratio * 100)}%` }}
              />
            </span>
          </div>
        );
        return it.href ? (
          <Link
            key={it.id}
            href={it.href}
            data-readiness={it.id}
            className="group block min-h-0 min-w-0 flex-1 rounded px-1 transition-colors hover:bg-[var(--surface)]"
          >
            {row}
          </Link>
        ) : (
          <div
            key={it.id}
            data-readiness={it.id}
            className="min-h-0 min-w-0 flex-1 px-1"
          >
            {row}
          </div>
        );
      })}
    </div>
  );
}
